import { createContext, useState, useContext, ReactNode } from 'react';

import {
  IFazenda,
  Roles,
  RolesProps,
  UserProps,
  RolesSlugs,
} from '@dinnisoft/esteio-gestao-common';
import {
  LoginPayloadRequestDTO,
  ValidateEmailPayloadRequestDTO,
  ValidateEmailResponseDTO,
} from 'dtos/AuthenticationDTO';

import { DATABASE_SELECTED, PROJECT_SELECTED, ROLE_SELECTED } from '../constants';
import {
  login,
  logout,
  getToken,
  getUser,
  getRoles,
  getDatabaseSelected,
  getProjectSelected,
  getSelectedFazenda,
  validateEmail,
} from '../services/auth';

interface AuthContextData {
  signed: boolean;
  user: UserProps | null;
  roles: RolesProps[];
  selectedRole: number;
  selectedFazenda: IFazenda;
  listProject: ValidateEmailResponseDTO[];
  selectedProject: string | null;
  selectedDatabase: string | null;
  refresh(): Promise<boolean>;
  validateEmail(payload: ValidateEmailPayloadRequestDTO): Promise<boolean>;
  selectProject(project: string, databaseName: string): Promise<boolean>;
  loginUser(payload: LoginPayloadRequestDTO): Promise<boolean>;
  logoutUser(): Promise<boolean>;
  selectRole(id: number): Promise<boolean>;
  getSlugFromRoleSelected(): RolesSlugs | '';
  userFromLocalStorage: UserProps;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const token: string | null = getToken();
  const roles = getRoles();
  const hasToken: boolean = token !== null && token !== '';
  const [listProject, setListProject] = useState<ValidateEmailResponseDTO[]>([]);
  const [selectedProject, setSelectedProject] = useState<string | null>(getProjectSelected());
  const [selectedDatabase, setSelectedDatabase] = useState<string | null>(getDatabaseSelected());
  const [signed, setSigned] = useState(hasToken);
  const userFromLocalStorage = getUser();
  const [user, setUser] = useState<UserProps | null>(userFromLocalStorage);

  let selectedRoleId = -1;

  if (Number(localStorage.getItem(ROLE_SELECTED)) > -1) {
    selectedRoleId = Number(localStorage.getItem(ROLE_SELECTED));
  }

  if (roles && roles.length && roles.length === 1) selectedRoleId = roles[0].id;

  const [selectedRole, setSelectedRole] = useState(selectedRoleId);

  const selectedFazenda = getSelectedFazenda();

  async function internalValidateEmail({
    email,
    reToken,
  }: ValidateEmailPayloadRequestDTO): Promise<boolean> {
    const response: ValidateEmailResponseDTO[] = await validateEmail({ email, reToken });
    setListProject(response);
    return response.length > 0;
  }

  async function internalSelectProject(project: string, databaseName: string): Promise<boolean> {
    if (project && databaseName) {
      setSelectedProject(project);
      setSelectedDatabase(databaseName);
      localStorage.setItem(PROJECT_SELECTED, project);
      localStorage.setItem(DATABASE_SELECTED, databaseName);
      return true;
    } else {
      setSelectedProject(null);
      setSelectedDatabase(null);
      localStorage.removeItem(PROJECT_SELECTED);
      localStorage.removeItem(DATABASE_SELECTED);
      return false;
    }
  }

  async function internalLogin({
    email,
    password,
    reToken,
  }: LoginPayloadRequestDTO): Promise<boolean> {
    const response: boolean = await login({ email, password, reToken });

    setSigned(response);

    const roles = getRoles();

    let selectedRoleId = -1;

    if (Number(localStorage.getItem(ROLE_SELECTED)) > -1) {
      selectedRoleId = Number(localStorage.getItem(ROLE_SELECTED));
    }

    if (roles && roles.length && roles.length === 1) {
      selectedRoleId = roles[0].id;
    }

    setSelectedRole(selectedRoleId);

    localStorage.setItem(ROLE_SELECTED, String(selectedRoleId));
    refresh();

    return response;
  }

  async function internalLogout(): Promise<boolean> {
    setSigned(false);
    setSelectedRole(-1);
    setListProject([]);
    setSelectedProject(null);
    setSelectedDatabase(null);
    localStorage.setItem(ROLE_SELECTED, '-1');
    const response: boolean = await logout();
    return response;
  }

  async function internalSetRoleSelect(id: number): Promise<boolean> {
    const response = false;
    setSelectedRole(id);
    localStorage.setItem(ROLE_SELECTED, String(id));
    return response;
  }

  function getSlugFromRoleSelected() {
    switch (selectedRole) {
      case Roles.admin:
        return 'admin';
      case Roles.consultor:
        return 'consultor';
      case Roles.produtor:
        return 'produtor';
      case Roles.gestor:
        return 'gestor';
      case Roles.gerente:
        return 'gerente';
      case Roles.central:
        return 'central';
      default:
        return '';
    }
  }

  async function refresh(): Promise<boolean> {
    const updatedUser = getUser();

    if (updatedUser) setUser(updatedUser);

    return true;
  }

  return (
    <AuthContext.Provider
      value={{
        signed,
        user,
        userFromLocalStorage,
        roles,
        selectedRole,
        selectedFazenda,
        listProject,
        selectedProject,
        selectedDatabase,
        validateEmail: internalValidateEmail,
        selectProject: internalSelectProject,
        loginUser: internalLogin,
        logoutUser: internalLogout,
        selectRole: internalSetRoleSelect,
        getSlugFromRoleSelected,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  return context;
}

export default AuthContext;
